import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import api, { getErrorMessage } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Package, ArrowUp, ArrowDown, Barcode, Warning, Clock, User, CurrencyDollar, TrendUp, TrendDown, Wallet, Receipt, FileXls, ShoppingCart, Trash, Plus, Printer, Download, X } from '@phosphor-icons/react';

const StockManagement = () => {
  const { activeBranch } = useBranch();
  const [stockItems, setStockItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  
  // Ledger states
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  
  const [formData, setFormData] = useState({
    barcode: '',
    transaction_type: 'in',
    quantity: 1,
    remarks: '',
    customer_id: '',
    transaction_date: new Date().toISOString().split('T')[0]
  });
  
  const [purchaseForm, setPurchaseForm] = useState({
    barcode: '',
    quantity: 1,
    price_per_unit: 0,
    paid_amount: 0,
    initial_payment_method: 'cash',
    supplier_name: ''
  });
  
  const [saleCart, setSaleCart] = useState([]);
  const [saleCustomer, setSaleCustomer] = useState('');
  const [salePayment, setSalePayment] = useState({
    amount_paid: 0,
    payment_method: 'cash'
  });
  const [currentItem, setCurrentItem] = useState({
    barcode: '',
    quantity: 1,
    price_per_unit: 0
  });
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    remarks: ''
  });

  const [selectedSale, setSelectedSale] = useState(null);
  const [showSalePaymentDialog, setShowSalePaymentDialog] = useState(false);
  const [salePaymentForm, setSalePaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    remarks: ''
  });
  
  const [expandedPurchase, setExpandedPurchase] = useState(null);
  const [expandedSale, setExpandedSale] = useState(null);

  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Cash' },
    { value: 'online', label: 'Online Transfer' },
    { value: 'upi', label: 'UPI' },
    { value: 'check', label: 'Cheque' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'other', label: 'Other' }
  ];
  
  // Filter states
  const [filters, setFilters] = useState({
    category: 'all',
    dateFrom: '',
    dateTo: '',
    searchQuery: ''
  });

  useEffect(() => {
    if (activeBranch) {
      fetchData();
      fetchCustomers();
      fetchLedgerData();
    }
  }, [activeBranch, filters.dateFrom, filters.dateTo]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const txnParams = { 
        branch_id: activeBranch.branch_id, 
        limit: 50 
      };
      
      // Add date filters if present
      if (filters.dateFrom) txnParams.from_date = filters.dateFrom;
      if (filters.dateTo) txnParams.to_date = filters.dateTo;
      
      const [stockRes, txnRes, alertsRes] = await Promise.all([
        api.get(`/stock/branch/${activeBranch.branch_id}`),
        api.get(`/stock/transactions`, { params: txnParams }),
        api.get(`/stock/branch/${activeBranch.branch_id}/low-stock`)
      ]);
      setStockItems(stockRes.data);
      setTransactions(txnRes.data);
      setLowStockAlerts(alertsRes.data.low_stock_items || []);
    } catch (error) {
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLedgerData = async () => {
    try {
      const [purchasesRes, salesRes, summaryRes] = await Promise.all([
        api.get('/ledger/purchases', { params: { branch_id: activeBranch.branch_id } }),
        api.get('/ledger/sales', { params: { branch_id: activeBranch.branch_id } }),
        api.get('/ledger/summary', { params: { branch_id: activeBranch.branch_id } })
      ]);
      setPurchases(purchasesRes.data);
      setSales(salesRes.data);
      setLedgerSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load ledger data:', error);
    }
  };

  const handleBarcodeInput = async (e) => {
    const barcode = e.target.value.trim();
    setFormData({...formData, barcode});
    
    if (barcode.length >= 6) {
      try {
        const response = await api.get(`/stock/barcode/${barcode}`);
        toast.success(`Item found: ${response.data.name}`);
      } catch (error) {
        if (error.response?.status === 404) {
          toast.error('Item not found with this barcode');
        }
      }
    }
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/stock/transaction', {
        ...formData,
        branch_id: activeBranch.branch_id
      });
      toast.success(`Stock ${formData.transaction_type === 'in' ? 'added' : 'removed'} successfully!`);
      setShowTransactionDialog(false);
      setFormData({ barcode: '', transaction_type: 'in', quantity: 1, remarks: '', customer_id: '', transaction_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Transaction failed'));
    }
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/ledger/purchase', {
        ...purchaseForm,
        branch_id: activeBranch.branch_id
      });
      toast.success('Purchase recorded successfully! Stock updated automatically.');
      setShowPurchaseDialog(false);
      setPurchaseForm({ barcode: '', quantity: 1, price_per_unit: 0, paid_amount: 0, initial_payment_method: 'cash', supplier_name: '' });
      fetchLedgerData();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Purchase failed'));
    }
  };

  const handleAddToCart = async () => {
    if (!currentItem.barcode) {
      toast.error('Please enter a barcode');
      return;
    }
    
    try {
      const response = await api.get(`/stock/barcode/${currentItem.barcode}`);
      const item = response.data;
      
      // Check if item already in cart
      const existingIndex = saleCart.findIndex(i => i.barcode === currentItem.barcode);
      if (existingIndex >= 0) {
        const updated = [...saleCart];
        updated[existingIndex].quantity += currentItem.quantity;
        updated[existingIndex].subtotal = updated[existingIndex].quantity * updated[existingIndex].price_per_unit;
        setSaleCart(updated);
      } else {
        setSaleCart([...saleCart, {
          barcode: item.barcode,
          item_name: item.name,
          quantity: currentItem.quantity,
          price_per_unit: currentItem.price_per_unit,
          subtotal: currentItem.quantity * currentItem.price_per_unit
        }]);
      }
      
      setCurrentItem({ barcode: '', quantity: 1, price_per_unit: 0 });
      toast.success(`${item.name} added to cart`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Item not found'));
    }
  };

  const handleRemoveFromCart = (index) => {
    setSaleCart(saleCart.filter((_, i) => i !== index));
  };

  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    
    if (saleCart.length === 0) {
      toast.error('Please add at least one item to cart');
      return;
    }
    
    try {
      const response = await api.post('/ledger/sale', {
        branch_id: activeBranch.branch_id,
        line_items: saleCart.map(item => ({
          barcode: item.barcode,
          quantity: item.quantity,
          price_per_unit: item.price_per_unit
        })),
        customer_id: saleCustomer || undefined,
        amount_paid: salePayment.amount_paid,
        payment_method: salePayment.payment_method
      });
      
      toast.success('Sale recorded successfully! Invoice generated.');
      setCurrentInvoice(response.data);
      setShowSaleDialog(false);
      setShowInvoiceModal(true);
      
      // Reset form
      setSaleCart([]);
      setSaleCustomer('');
      setSalePayment({ amount_paid: 0, payment_method: 'cash' });
      setCurrentItem({ barcode: '', quantity: 1, price_per_unit: 0 });
      
      fetchLedgerData();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Sale failed'));
    }
  };

  const viewInvoice = async (saleId) => {
    try {
      const response = await api.get(`/ledger/sale/${saleId}/invoice`);
      setCurrentInvoice(response.data);
      setShowInvoiceModal(true);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const printInvoice = () => {
    window.print();
  };

  const downloadInvoice = () => {
    const invoiceContent = document.getElementById('invoice-content');
    if (invoiceContent) {
      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write('<html><head><title>Invoice</title>');
      printWindow.document.write('<style>body{font-family:Arial,sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ddd;padding:8px;text-align:left;}</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(invoiceContent.innerHTML);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getCartTotal = () => {
    return saleCart.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/ledger/purchase/${selectedPurchase.purchase_id}/payment`, paymentForm);
      toast.success('Payment recorded successfully!');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
      setSelectedPurchase(null);
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Payment failed'));
    }
  };

  const openPaymentDialog = (purchase) => {
    setSelectedPurchase(purchase);
    setPaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
    setShowPaymentDialog(true);
  };

  const togglePaymentHistory = (purchaseId) => {
    setExpandedPurchase(expandedPurchase === purchaseId ? null : purchaseId);
  };

  const toggleSalePaymentHistory = (saleId) => {
    setExpandedSale(expandedSale === saleId ? null : saleId);
  };

  const openSalePaymentDialog = (sale) => {
    setSelectedSale(sale);
    setSalePaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
    setShowSalePaymentDialog(true);
  };

  const handleSalePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/ledger/sale/${selectedSale.sale_id}/payment`, salePaymentForm);
      toast.success('Payment collected successfully!');
      setShowSalePaymentDialog(false);
      setSalePaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
      setSelectedSale(null);
      fetchLedgerData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Payment failed'));
    }
  };

  const exportToExcel = (data, filename) => {
    // Simple CSV export
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => Object.values(item).map(val => `"${val}"`).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported successfully!');
  };

  // Get unique categories from stock items and transactions
  const getUniqueCategories = () => {
    const categories = new Set();
    stockItems.forEach(item => {
      const category = (item.category || 'Other').trim();
      categories.add(category);
    });
    transactions.forEach(txn => {
      const category = (txn.category || 'Other').trim();
      categories.add(category);
    });
    return ['all', ...Array.from(categories).sort()];
  };

  // Filter stock items based on filters
  const getFilteredStockItems = () => {
    let filtered = [...stockItems];
    
    if (filters.category !== 'all') {
      filtered = filtered.filter(item => {
        const itemCategory = (item.category || 'Other').trim();
        return itemCategory === filters.category;
      });
    }
    
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.item_name.toLowerCase().includes(query) ||
        item.barcode.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  // Filter transactions based on filters
  const getFilteredTransactions = () => {
    let filtered = [...transactions];
    
    if (filters.category !== 'all') {
      filtered = filtered.filter(txn => {
        const txnCategory = (txn.category || 'Other').trim();
        return txnCategory === filters.category;
      });
    }
    
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(txn => new Date(txn.created_at) >= fromDate);
    }
    
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(txn => new Date(txn.created_at) <= toDate);
    }
    
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(txn => 
        txn.item_name.toLowerCase().includes(query) ||
        txn.barcode.toLowerCase().includes(query) ||
        (txn.customer_name && txn.customer_name.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };

  // Filter low stock alerts based on filters
  const getFilteredLowStockAlerts = () => {
    let filtered = [...lowStockAlerts];
    
    if (filters.category !== 'all') {
      filtered = filtered.filter(alert => {
        const alertCategory = (alert.category || 'Other').trim();
        return alertCategory === filters.category;
      });
    }
    
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(alert => 
        alert.item_name.toLowerCase().includes(query) ||
        alert.barcode.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const resetFilters = () => {
    setFilters({
      category: 'all',
      dateFrom: '',
      dateTo: '',
      searchQuery: ''
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading stock data...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="stock-management-page">
        {/* Low Stock Alerts */}
        {lowStockAlerts.length > 0 && (
          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-start gap-3">
              <Warning size={24} className="text-orange-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 mb-2">Low Stock Alert</h3>
                <div className="space-y-1 text-sm">
                  {lowStockAlerts.map((alert) => (
                    <div key={alert.item_id} className="flex justify-between text-orange-800">
                      <span>{alert.item_name}</span>
                      <span className="font-medium">
                        {alert.current_quantity}/{alert.min_quantity} {alert.unit} (Need {alert.shortage} more)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        <Tabs defaultValue="stock" className="w-full">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <TabsList>
              <TabsTrigger value="stock">Current Stock</TabsTrigger>
              <TabsTrigger value="transactions">Transaction History</TabsTrigger>
              <TabsTrigger value="alerts">Low Stock Alerts</TabsTrigger>
            </TabsList>
            <Button onClick={() => setShowTransactionDialog(true)} className="w-full sm:w-auto bg-[#2C4C3B] hover:bg-[#2C4C3B]/90">
              <Package size={20} className="mr-2" />
              Stock In/Out
            </Button>
          </div>

          {/* Filters Section */}
          <Card className="p-4 sm:p-6 mb-6">
            <h3 className="text-sm font-semibold text-[#2C4C3B] mb-4 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Category Filter */}
              <div>
                <Label className="text-xs mb-2 block">Category</Label>
                <Select 
                  value={filters.category} 
                  onValueChange={(value) => {
                    console.log('Category selected:', value);
                    console.log('Available categories:', getUniqueCategories());
                    setFilters({...filters, category: value});
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select category">
                      {filters.category === 'all' ? 'All Categories' : filters.category}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {getUniqueCategories().map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {cat === 'all' ? 'All Categories' : cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(filters.category !== 'all' || filters.searchQuery) && (
                  <div className="text-xs text-[#1C1C1A]/60 mt-1">
                    Showing {getFilteredStockItems().length} of {stockItems.length} items
                  </div>
                )}
              </div>

              {/* Date From */}
              <div>
                <Label className="text-xs mb-2 block">Date From</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                  className="w-full"
                />
              </div>

              {/* Date To */}
              <div>
                <Label className="text-xs mb-2 block">Date To</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                  className="w-full"
                />
              </div>

              {/* Search */}
              <div>
                <Label className="text-xs mb-2 block">Search</Label>
                <Input
                  type="text"
                  placeholder="Item name, barcode..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({...filters, searchQuery: e.target.value})}
                  className="w-full"
                />
              </div>
            </div>

            {/* Reset Button */}
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="text-xs"
              >
                Reset Filters
              </Button>
            </div>
          </Card>

          <TabsContent value="stock" className="mt-6">
            {getFilteredStockItems().length === 0 ? (
              <Card className="p-12 text-center">
                <Package size={64} className="mx-auto mb-4 text-[#1C1C1A]/20" />
                <p className="text-[#1C1C1A]/60">
                  {filters.category !== 'all' || filters.searchQuery ? 'No items match your filters' : 'No stock items available'}
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                {Object.entries(
                  getFilteredStockItems().reduce((acc, item) => {
                    const category = item.category || 'Other';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(item);
                    return acc;
                  }, {})
                ).map(([category, items]) => (
                  <div key={category}>
                    <h3 className="text-lg font-semibold text-[#2C4C3B] mb-3 flex items-center gap-2">
                      <Package size={20} />
                      {category}
                      <Badge variant="outline" className="ml-2">{items.length} items</Badge>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.map((item) => (
                        <Card key={item.branch_stock_id} className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-semibold text-lg text-[#2C4C3B]">{item.item_name}</h3>
                              <p className="text-xs text-[#1C1C1A]/60 font-mono flex items-center gap-1 mt-1">
                                <Barcode size={14} />
                                {item.barcode}
                              </p>
                            </div>
                            {item.is_low_stock && (
                              <Badge variant="destructive" className="bg-orange-100 text-orange-700 border-orange-300">
                                Low
                              </Badge>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between items-center py-3 border-t border-b border-[#E5E5DF]">
                              <span className="text-2xl font-bold text-[#2C4C3B]">{item.quantity}</span>
                              <span className="text-sm text-[#1C1C1A]/60">{item.unit}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-[#1C1C1A]/60">Minimum:</span>
                              <span className="font-medium">{item.min_quantity} {item.unit}</span>
                            </div>
                            <div className="text-xs text-[#1C1C1A]/40 flex items-center gap-1">
                              <Clock size={12} />
                              Updated: {new Date(item.last_updated).toLocaleDateString()}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="transactions" className="mt-6">
            <Card>
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-4">Recent Transactions</h3>
                {getFilteredTransactions().length === 0 ? (
                  <p className="text-center text-[#1C1C1A]/60 py-8">
                    {filters.category !== 'all' || filters.dateFrom || filters.dateTo || filters.searchQuery 
                      ? 'No transactions match your filters' 
                      : 'No transactions yet'}
                  </p>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(
                      getFilteredTransactions().reduce((acc, txn) => {
                        const category = txn.category || 'Other';
                        if (!acc[category]) acc[category] = [];
                        acc[category].push(txn);
                        return acc;
                      }, {})
                    ).map(([category, categoryTxns]) => (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-lg font-semibold text-[#2C4C3B] flex items-center gap-2">
                            <Package size={20} />
                            {category}
                            <Badge variant="outline" className="ml-2">{categoryTxns.length} transactions</Badge>
                          </h4>
                        </div>
                        <div className="space-y-3">
                          {categoryTxns.map((txn) => (
                            <div key={txn.transaction_id} className="flex items-start gap-4 p-4 border border-[#E5E5DF] rounded-md bg-white hover:shadow-sm transition-shadow">
                              <div className={`p-2 rounded-full ${txn.transaction_type === 'in' ? 'bg-green-100' : 'bg-red-100'}`}>
                                {txn.transaction_type === 'in' ? (
                                  <ArrowDown size={20} className="text-green-600" />
                                ) : (
                                  <ArrowUp size={20} className="text-red-600" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between items-start mb-1">
                                  <div>
                                    <h4 className="font-medium text-[#2C4C3B]">{txn.item_name}</h4>
                                    <p className="text-sm text-[#1C1C1A]/60">{txn.barcode}</p>
                                  </div>
                                  <div className="text-right">
                                    <div className={`text-lg font-bold ${txn.transaction_type === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                                      {txn.transaction_type === 'in' ? '+' : '-'}{txn.quantity}
                                    </div>
                                    <div className="text-xs text-[#1C1C1A]/60">Balance: {txn.remaining_quantity}</div>
                                  </div>
                                </div>
                                {txn.remarks && (
                                  <p className="text-sm text-[#1C1C1A]/70 italic mt-1">{txn.remarks}</p>
                                )}
                                {txn.customer_name && (
                                  <div className="flex items-center gap-1 text-sm text-blue-700 mt-1">
                                    <User size={14} />
                                    <span>To: {txn.customer_name}</span>
                                  </div>
                                )}
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E5E5DF]/50">
                                  <div className="text-xs text-[#1C1C1A]/40">
                                    {txn.created_by_name}
                                  </div>
                                  <div className="text-xs text-[#1C1C1A]/60 font-medium">
                                    {new Date(txn.created_at).toLocaleDateString('en-IN', { 
                                      day: '2-digit', 
                                      month: 'short', 
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Low Stock Alerts Tab */}
          <TabsContent value="alerts" className="mt-6">
            <Card>
              <div className="p-6">
                <h3 className="font-semibold text-lg mb-4">Low Stock Alerts</h3>
                {getFilteredLowStockAlerts().length === 0 ? (
                  <p className="text-center text-[#1C1C1A]/60 py-8">
                    {filters.category !== 'all' || filters.searchQuery 
                      ? 'No alerts match your filters' 
                      : 'No low stock alerts'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {getFilteredLowStockAlerts().map((alert) => (
                      <div key={alert.item_id} className="flex justify-between items-center p-4 border border-orange-200 rounded-md bg-orange-50">
                        <div>
                          <h4 className="font-medium text-orange-900">{alert.item_name}</h4>
                          <p className="text-sm text-orange-700">{alert.barcode}</p>
                          <p className="text-xs text-orange-600 mt-1">{alert.category || 'Other'}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-orange-600">
                            {alert.current_quantity} / {alert.min_quantity} {alert.unit}
                          </div>
                          <div className="text-xs text-orange-700">
                            Need {alert.shortage} more
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

        </Tabs>

        {/* Transaction Dialog */}
        <Dialog open={showTransactionDialog} onOpenChange={setShowTransactionDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Stock Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleTransactionSubmit} className="space-y-4">
              <div>
                <Label>Barcode *</Label>
                <Input
                  required
                  value={formData.barcode}
                  onChange={handleBarcodeInput}
                  placeholder="Scan or enter barcode"
                  data-testid="transaction-barcode-input"
                  autoFocus
                />
              </div>

              <div>
                <Label>Transaction Type *</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formData.transaction_type === 'in' ? 'default' : 'outline'}
                    onClick={() => setFormData({...formData, transaction_type: 'in'})}
                    className={`flex-1 ${formData.transaction_type === 'in' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    <ArrowDown size={20} className="mr-2" />
                    Stock In
                  </Button>
                  <Button
                    type="button"
                    variant={formData.transaction_type === 'out' ? 'default' : 'outline'}
                    onClick={() => setFormData({...formData, transaction_type: 'out'})}
                    className={`flex-1 ${formData.transaction_type === 'out' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  >
                    <ArrowUp size={20} className="mr-2" />
                    Stock Out
                  </Button>
                </div>
              </div>

              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  required
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value)})}
                  data-testid="transaction-quantity-input"
                />
              </div>

              {formData.transaction_type === 'out' && (
                <div>
                  <Label>To: Which Client (Optional)</Label>
                  <Select
                    value={formData.customer_id || undefined}
                    onValueChange={(value) => setFormData({...formData, customer_id: value || ''})}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select customer (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.customer_id} value={customer.customer_id}>
                          {customer.name} - REG {customer.reg_no}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.customer_id && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({...formData, customer_id: ''})}
                      className="mt-2 text-xs"
                    >
                      Clear Selection
                    </Button>
                  )}
                </div>
              )}

              <div>
                <Label>Remarks</Label>
                <Input
                  value={formData.remarks}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  placeholder="Optional notes"
                />
              </div>

              <div>
                <Label>Transaction Date *</Label>
                <Input
                  type="date"
                  required
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({...formData, transaction_date: e.target.value})}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowTransactionDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Submit Transaction
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Purchase Dialog */}
        <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Record Purchase</DialogTitle>
              <p className="text-sm text-[#1C1C1A]/60">Stock will be automatically updated</p>
            </DialogHeader>
            <form onSubmit={handlePurchaseSubmit} className="space-y-4">
              <div>
                <Label>Barcode *</Label>
                <Input
                  required
                  value={purchaseForm.barcode}
                  onChange={(e) => setPurchaseForm({...purchaseForm, barcode: e.target.value})}
                  placeholder="Scan or enter barcode"
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    required
                    min="1"
                    value={purchaseForm.quantity}
                    onChange={(e) => setPurchaseForm({...purchaseForm, quantity: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Price per Unit *</Label>
                  <Input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={purchaseForm.price_per_unit}
                    onChange={(e) => setPurchaseForm({...purchaseForm, price_per_unit: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-md">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-blue-700">Total Amount:</span>
                  <span className="font-bold text-blue-900">₹{(purchaseForm.quantity * purchaseForm.price_per_unit).toLocaleString()}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount Paid (Initial Payment)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={purchaseForm.paid_amount}
                    onChange={(e) => setPurchaseForm({...purchaseForm, paid_amount: parseFloat(e.target.value)})}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Payment Method</Label>
                  <Select 
                    value={purchaseForm.initial_payment_method} 
                    onValueChange={(val) => setPurchaseForm({...purchaseForm, initial_payment_method: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Supplier Name</Label>
                <Input
                  value={purchaseForm.supplier_name}
                  onChange={(e) => setPurchaseForm({...purchaseForm, supplier_name: e.target.value})}
                  placeholder="Supplier name (optional)"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPurchaseDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Record Purchase
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Sale Dialog - Multi-Product Cart */}
        <Dialog open={showSaleDialog} onOpenChange={setShowSaleDialog}>
          <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B] flex items-center gap-2">
                <ShoppingCart size={24} />
                Record Multi-Product Sale
              </DialogTitle>
              <p className="text-sm text-[#1C1C1A]/60">Add items to cart, then complete the sale with payment details</p>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Customer Selection */}
              <div>
                <Label>Customer</Label>
                <Select value={saleCustomer} onValueChange={setSaleCustomer}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.customer_id} value={customer.customer_id}>
                        {customer.name} - REG {customer.reg_no}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Add Item Section */}
              <Card className="p-4 bg-blue-50">
                <h4 className="font-semibold mb-3 text-[#2C4C3B]">Add Item to Cart</h4>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-5">
                    <Input
                      placeholder="Barcode"
                      value={currentItem.barcode}
                      onChange={(e) => setCurrentItem({...currentItem, barcode: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      placeholder="Qty"
                      min="1"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem({...currentItem, quantity: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      placeholder="Price"
                      min="0"
                      step="0.01"
                      value={currentItem.price_per_unit}
                      onChange={(e) => setCurrentItem({...currentItem, price_per_unit: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div className="col-span-2">
                    <Button type="button" onClick={handleAddToCart} className="w-full bg-[#2C4C3B]">
                      <Plus size={20} />
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Cart Items */}
              {saleCart.length > 0 && (
                <Card className="p-4">
                  <h4 className="font-semibold mb-3 text-[#2C4C3B]">Cart ({saleCart.length} items)</h4>
                  <div className="space-y-2">
                    {saleCart.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-[#F5F5F0] rounded">
                        <div className="flex-1">
                          <div className="font-medium">{item.item_name}</div>
                          <div className="text-sm text-[#1C1C1A]/60">
                            {item.quantity} × ₹{item.price_per_unit.toLocaleString()} = ₹{item.subtotal.toLocaleString()}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handleRemoveFromCart(index)} className="text-red-600">
                          <Trash size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#E5E5DF]">
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total Amount:</span>
                      <span className="text-[#2C4C3B]">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                  </div>
                </Card>
              )}

              {/* Payment Section */}
              {saleCart.length > 0 && (
                <Card className="p-4 bg-green-50">
                  <h4 className="font-semibold mb-3 text-[#2C4C3B]">Payment Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Amount Paid (Booking Amount)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={salePayment.amount_paid}
                        onChange={(e) => setSalePayment({...salePayment, amount_paid: parseFloat(e.target.value) || 0})}
                        placeholder="Enter amount received"
                      />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <Select value={salePayment.payment_method} onValueChange={(val) => setSalePayment({...salePayment, payment_method: val})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_METHODS.map(method => (
                            <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-white rounded">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Total Amount:</span>
                      <span className="font-medium">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Amount Paid:</span>
                      <span className="font-medium text-green-700">₹{salePayment.amount_paid.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold pt-2 border-t">
                      <span>Remaining Balance:</span>
                      <span className={`${(getCartTotal() - salePayment.amount_paid) > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                        ₹{(getCartTotal() - salePayment.amount_paid).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Card>
              )}

              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowSaleDialog(false);
                    setSaleCart([]);
                    setSaleCustomer('');
                    setSalePayment({ amount_paid: 0, payment_method: 'cash' });
                  }} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={handleSaleSubmit} 
                  disabled={saleCart.length === 0}
                  className="flex-1 bg-[#2C4C3B]"
                >
                  <Receipt size={20} className="mr-2" />
                  Record Sale & Generate Invoice
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invoice Modal */}
        <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
          <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto print:max-w-full">
            <DialogHeader className="print:hidden">
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Invoice</DialogTitle>
              <div className="flex gap-2">
                <Button size="sm" onClick={printInvoice} variant="outline">
                  <Printer size={16} className="mr-2" />
                  Print
                </Button>
                <Button size="sm" onClick={downloadInvoice} variant="outline">
                  <Download size={16} className="mr-2" />
                  Download
                </Button>
              </div>
            </DialogHeader>
            
            {currentInvoice && (
              <div id="invoice-content" className="p-8 bg-white">
                {/* Invoice Header */}
                <div className="text-center mb-6 pb-4 border-b-2 border-[#2C4C3B]">
                  <h1 className="text-3xl font-bold text-[#2C4C3B]">INVOICE</h1>
                  <div className="text-lg font-semibold mt-2">{currentInvoice.branch_name}</div>
                  <div className="text-sm text-[#1C1C1A]/60 mt-1">Therapy & Wellness Center</div>
                </div>

                {/* Invoice Info */}
                <div className="grid grid-cols-2 gap-8 mb-6">
                  <div>
                    <h3 className="font-semibold text-[#2C4C3B] mb-2">Bill To:</h3>
                    <div className="text-sm">
                      <div className="font-medium">{currentInvoice.customer_name}</div>
                      {currentInvoice.customer_reg_no && <div>REG: {currentInvoice.customer_reg_no}</div>}
                      {currentInvoice.customer_phone && <div>Phone: {currentInvoice.customer_phone}</div>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">
                      <div className="font-semibold text-lg">{currentInvoice.invoice_number}</div>
                      <div className="mt-2">Date: {currentInvoice.sale_date}</div>
                      <div>Time: {new Date(currentInvoice.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <table className="w-full mb-6 border-collapse">
                  <thead>
                    <tr className="bg-[#E5E5DF]">
                      <th className="p-3 text-left border">#</th>
                      <th className="p-3 text-left border">Item Description</th>
                      <th className="p-3 text-center border">Quantity</th>
                      <th className="p-3 text-right border">Price</th>
                      <th className="p-3 text-right border">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentInvoice.line_items?.map((item, index) => (
                      <tr key={index}>
                        <td className="p-3 border">{index + 1}</td>
                        <td className="p-3 border">
                          <div className="font-medium">{item.item_name}</div>
                          <div className="text-xs text-[#1C1C1A]/60">Code: {item.barcode}</div>
                        </td>
                        <td className="p-3 text-center border">{item.quantity} {item.unit}</td>
                        <td className="p-3 text-right border">₹{item.price_per_unit.toLocaleString()}</td>
                        <td className="p-3 text-right border font-medium">₹{item.subtotal.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64">
                    <div className="flex justify-between py-2 border-b">
                      <span className="font-medium">Subtotal:</span>
                      <span>₹{currentInvoice.total_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="font-semibold text-lg">Total Amount:</span>
                      <span className="font-bold text-lg text-[#2C4C3B]">₹{currentInvoice.total_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b text-green-700">
                      <span className="font-medium">Amount Paid ({currentInvoice.payment_method?.toUpperCase()}):</span>
                      <span className="font-medium">₹{currentInvoice.amount_paid?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 font-bold">
                      <span>Balance Due:</span>
                      <span className={currentInvoice.remaining_balance > 0 ? 'text-orange-700' : 'text-green-700'}>
                        ₹{currentInvoice.remaining_balance?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-4 border-t text-center text-sm text-[#1C1C1A]/60">
                  <p>Thank you for your business!</p>
                  <p className="mt-2">Recorded by: {currentInvoice.created_by_name}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sale Payment Dialog (Collect Balance) */}
        <Dialog open={showSalePaymentDialog} onOpenChange={setShowSalePaymentDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Collect Balance Payment</DialogTitle>
              {selectedSale && (
                <div className="mt-2 text-sm">
                  <p className="text-[#1C1C1A]/70">Invoice: {selectedSale.invoice_number}</p>
                  <p className="text-[#1C1C1A]/70">Customer: {selectedSale.customer_name}</p>
                  <p className="text-orange-700 font-medium">Balance Due: ₹{selectedSale.remaining_balance?.toLocaleString()}</p>
                </div>
              )}
            </DialogHeader>
            <form onSubmit={handleSalePaymentSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Amount *</Label>
                  <Input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    max={selectedSale?.remaining_balance}
                    value={salePaymentForm.amount}
                    onChange={(e) => setSalePaymentForm({...salePaymentForm, amount: parseFloat(e.target.value)})}
                    placeholder="Enter amount"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Payment Method *</Label>
                  <Select 
                    value={salePaymentForm.payment_method} 
                    onValueChange={(val) => setSalePaymentForm({...salePaymentForm, payment_method: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Remarks</Label>
                <Input
                  value={salePaymentForm.remarks}
                  onChange={(e) => setSalePaymentForm({...salePaymentForm, remarks: e.target.value})}
                  placeholder="Payment notes (optional)"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowSalePaymentDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Collect Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Add Payment</DialogTitle>
              {selectedPurchase && (
                <div className="mt-2 text-sm">
                  <p className="text-[#1C1C1A]/70">Purchase: {selectedPurchase.item_name}</p>
                  <p className="text-orange-700 font-medium">Remaining Balance: ₹{selectedPurchase.remaining_balance.toLocaleString()}</p>
                </div>
              )}
            </DialogHeader>
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Amount *</Label>
                  <Input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    max={selectedPurchase?.remaining_balance}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value)})}
                    placeholder="Enter payment amount"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Payment Method *</Label>
                  <Select 
                    value={paymentForm.payment_method} 
                    onValueChange={(val) => setPaymentForm({...paymentForm, payment_method: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Remarks</Label>
                <Input
                  value={paymentForm.remarks}
                  onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})}
                  placeholder="Payment notes (optional)"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Add Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StockManagement;
